/* eslint-disable indent */
import * as React from 'react';
import { useCallback, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Dialog,
  InputAdornment,
  DialogTitle,
  DialogActions,
  DialogContent,
  TextField
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { FormattedMessage, useIntl } from 'react-intl';
import { useUIContext } from 'modules/common/hooks/useUIContext';
import { ErrorPage } from 'modules/common/routes/ErrorPage';
import { useMessages } from 'modules/intl';
import { postRequest } from 'utils/helper';
import { OauthPopupLoginAndRedirect } from 'modules/oauth/oauthPopupLoginAndRedirect';
import { encryptDataWithAES } from 'utils/hmac-auth';
import { ConnectorInfo } from 'types/types';
import { DialogHeaderContext } from './DialogHeader';

const useStyles = makeStyles((theme) => ({
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: theme.spacing(2.5)
  },
  box: {
    display: 'flex',
    justifyContent: 'center'
  },
  image: {
    height: theme.spacing(12.5),
    width: theme.spacing(18.75)
  },
  textField: {
    marginBottom: theme.spacing(3.75)
  }
}));

type ZendeskInstallDialogPageProps = {
  info: ConnectorInfo;
  open: boolean;
  handleInstallDialogClose: () => void;
  setIsSignInAlertOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

const messages = ['intermediateOauthDialog.inputError'];

export const ZendeskInstallDialogPage: React.FC<ZendeskInstallDialogPageProps> = ({
  info,
  open,
  handleInstallDialogClose,
  setIsSignInAlertOpen
}: ZendeskInstallDialogPageProps) => {
  const { formatMessage } = useIntl();
  const [input, setInput] = useState({
    clientId: '',
    clientSecret: '',
    hostName: ''
  });
  const [inputError, setInputError] = useState({
    clientIdError: '',
    clientSecretError: '',
    hostNameError: ''
  });
  const [connected, setConnected] = useState(false);
  const [showErrorPage, setShowErrorPage] = useState(false);
  const { data } = useUIContext();
  const classes = useStyles();
  const errorMessage = useMessages(messages);

  const requiredInputError = errorMessage['intermediateOauthDialog.inputError'];

  const handleError = useCallback(
    (value: string, textfieldName: string) => {
      setInputError((prev: any) => ({
        ...prev,
        [textfieldName]: value.trim() === '' ? requiredInputError : ''
      }));
    },
    [requiredInputError]
  );
  const handleText = useCallback(
    (event: any, textfieldName: string, textfieldErrorName: string) => {
      const value = event.target.value;
      setInput((prev: any) => ({
        ...prev,
        [textfieldName]: value
      }));
      handleError(value, textfieldErrorName);
    },
    [handleError]
  );

  const handleApiCall = useCallback(async () => {
    try {
      const token = {
        client_id: input.clientId,
        client_secret: input.clientSecret
      };
      const jsonToken = JSON.stringify(token);
      const body = {
        connectorName: info.shortName,
        companyKey: info.companyKey,
        environment: 'production',
        isPsaProduct: info.isPsaProduct,
        hostName: input.hostName,
        token: encryptDataWithAES(jsonToken, data.installationID)
      };
      const res = await postRequest(
        '/integration-settings-api/install-connector',
        body,
        false
      );
      if (res.data) {
        setConnected(true);
        setIsSignInAlertOpen(true);
        OauthPopupLoginAndRedirect(res.data, info.name);
      }
    } catch (error: any) {
      if (error.response) {
        setShowErrorPage(true);
      }
    }
  }, [
    input.clientId,
    input.clientSecret,
    input.hostName,
    info.shortName,
    info.companyKey,
    info.isPsaProduct,
    info.name,
    data.installationID,
    setIsSignInAlertOpen
  ]);

  const validateAndConnect = (event: any) => {
    event.preventDefault();
    const value = event.target.value;

    const fields: Array<keyof typeof input> = [
      'hostName',
      'clientId',
      'clientSecret'
    ];
    let hasError = false;

    fields.forEach((field) => {
      if (input[field].trim() === '') {
        handleError(value, `${field}Error`);
        hasError = true;
      }
    });

    if (!hasError) {
      handleApiCall();
    }
  };

  /* eslint-disable indent */
  const helperText = useCallback(
    (textfieldId: string) => {
      switch (textfieldId) {
        case 'hostName':
          return formatMessage({
            id: 'intermediateOauthDialog.hostName.zendesk.helperText'
          });
        case 'clientId':
          return formatMessage({
            id: 'intermediateOauthDialog.zendesk.clientIdHelperText'
          });
        case 'clientSecret':
          return formatMessage({
            id: 'intermediateOauthDialog.zendesk.clientSecretHelperText'
          });
      }
    },
    [formatMessage]
  );

  const endAdornment = useMemo(() => {
    if (info.shortName === 'zendesk') {
      return formatMessage({
        id: 'intermediateOauthDialog.hostName.zendesk.endAdornment'
      });
    }
  }, [formatMessage, info.shortName]);

  setTimeout(() => {
    if (connected) {
      handleInstallDialogClose();
      setConnected(false);
    }
  }, 2000);

  if (showErrorPage) {
    return <ErrorPage open={true} />;
  }

  return (
    <>
      <Dialog
        open={open}
        onClose={handleInstallDialogClose}
        data-testid="render-all-contents"
      >
        <DialogTitle
          className={classes.header}
          data-testid="render-dialog-title"
        >
          <FormattedMessage id={'intermediateOauthDialog.header'} />
        </DialogTitle>
        <DialogContent>
          <DialogHeaderContext shortName={info.shortName} />
          <Box className={classes.box} data-testid="render-box">
            <div>
              <br />
              <TextField
                id="hostName"
                variant="outlined"
                required
                fullWidth
                value={input.hostName}
                onChange={(event) =>
                  handleText(event, 'hostName', 'hostNameError')
                }
                error={Boolean(inputError.hostNameError)}
                label={
                  <FormattedMessage
                    id={'intermediateOauthDialog.hostName.label'}
                  />
                }
                type={'text'}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Typography
                        variant="body1"
                        data-testid="check-hostname-startAdornment"
                      >
                        <FormattedMessage
                          id={'intermediateOauthDialog.hostName.startAdornment'}
                        />
                      </Typography>
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <Typography
                        variant="body1"
                        data-testid="check-hostname-endAdornment"
                      >
                        {endAdornment}
                      </Typography>
                    </InputAdornment>
                  )
                }}
                helperText={
                  inputError.hostNameError
                    ? inputError.hostNameError
                    : helperText('hostName')
                }
                className={classes.textField}
                inputProps={{ 'data-testid': 'hostNameInput' }}
              />
              <br />
              <TextField
                id="clientId"
                variant="outlined"
                required
                fullWidth
                value={input.clientId}
                onChange={(event) =>
                  handleText(event, 'clientId', 'clientIdError')
                }
                error={Boolean(inputError.clientIdError)}
                label={
                  <FormattedMessage
                    id={'intermediateOauthDialog.zendesk.clientIdLabel'}
                  />
                }
                type={'text'}
                InputLabelProps={{
                  shrink: true
                }}
                helperText={
                  inputError.clientIdError
                    ? inputError.clientIdError
                    : helperText('clientId')
                }
                className={classes.textField}
                inputProps={{ 'data-testid': 'clientIdInput' }}
              />
              <br />
              <TextField
                id="clientSecret"
                variant="outlined"
                required
                fullWidth
                value={input.clientSecret}
                onChange={(event) =>
                  handleText(event, 'clientSecret', 'clientSecretError')
                }
                error={Boolean(inputError.clientSecretError)}
                label={
                  <FormattedMessage
                    id={'intermediateOauthDialog.zendesk.clientSecretLabel'}
                  />
                }
                type={'password'}
                InputLabelProps={{
                  shrink: true
                }}
                helperText={
                  inputError.clientSecretError
                    ? inputError.clientSecretError
                    : helperText('clientSecret')
                }
                className={classes.textField}
                inputProps={{ 'data-testid': 'clientSecretInput' }}
              />
              <br />
            </div>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleInstallDialogClose}
            color="grey"
            autoFocus
            variant="contained"
            data-testid="cancel-button"
          >
            <FormattedMessage id={'connectorSetting.cancel'} />
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            onClick={validateAndConnect}
            data-testid="connect-button"
          >
            <FormattedMessage id={'intermediateOauthDialog.connectButton'} />
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
